import { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Textarea,
  Spinner,
  useDisclosure,
} from '@heroui/react'

import {
  TestQueryService,
  type TestQuery,
  type TestQueryInput,
  type TestRun,
} from '../services/testQueryService'
import {
  RecommendationLogsService,
  type RecommendationLogEntry,
} from '../services/recommendationLogsService'
import { useToast } from '../../../hooks'
import { copyToClipboard } from '../../gifs/services'
import { useGifs } from '../../gifs/hooks'
import { gifDisplayTitle } from '../../gifs/types'

import { PerformanceMetrics } from './PerformanceMetrics'
import { SearchCandidatesPreview } from './SearchCandidatesPreview'
import { GifThumbnail } from './GifThumbnail'

interface TestQueryFormData {
  description: string
  conversationSnippet: string
  userIntent: string
  tags: string[]
}

export function RecommendationTesting() {
  const [activeTab, setActiveTab] = useState('queries')
  const [testQueries, setTestQueries] = useState<TestQuery[]>([])
  const [testRuns, setTestRuns] = useState<TestRun[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedQueries, setSelectedQueries] = useState<Set<string>>(new Set())
  const [editingQuery, setEditingQuery] = useState<TestQuery | null>(null)
  const [viewingTestRun, setViewingTestRun] = useState<TestRun | null>(null)
  const [testRunLogs, setTestRunLogs] = useState<RecommendationLogEntry[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)

  // Get GIFs for thumbnail display
  const { gifs } = useGifs()

  // Form states
  const [formData, setFormData] = useState<TestQueryFormData>({
    description: '',
    conversationSnippet: '',
    userIntent: '',
    tags: [],
  })
  const [tagInput, setTagInput] = useState('')
  const [testRunName, setTestRunName] = useState('')
  const [testRunDescription, setTestRunDescription] = useState('')

  // Modals
  const {
    isOpen: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure()
  const {
    isOpen: isRunModalOpen,
    onOpen: onRunModalOpen,
    onClose: onRunModalClose,
  } = useDisclosure()
  const {
    isOpen: isResultsModalOpen,
    onOpen: onResultsModalOpen,
    onClose: onResultsModalClose,
  } = useDisclosure()

  const toast = useToast()

  // Load test queries
  const loadTestQueries = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await TestQueryService.getTestQueries({ limit: 100 })
      setTestQueries(result.queries)
    } catch (error) {
      toast.error(
        `Failed to load test queries: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  // Load test runs
  const loadTestRuns = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await TestQueryService.getTestRuns({ limit: 50 })
      setTestRuns(result.testRuns)
    } catch (error) {
      toast.error(
        `Failed to load test runs: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error('Description is required')
      return
    }

    if (!formData.conversationSnippet.trim() && !formData.userIntent.trim()) {
      toast.error('Either conversation snippet or user intent is required')
      return
    }

    if (formData.tags.length === 0) {
      toast.error('At least one tag is required')
      return
    }

    try {
      const input: TestQueryInput = {
        description: formData.description.trim(),
        conversationSnippet: formData.conversationSnippet.trim() || undefined,
        userIntent: formData.userIntent.trim() || undefined,
        tags: formData.tags,
      }

      if (editingQuery) {
        await TestQueryService.updateTestQuery(editingQuery.id!, input)
        toast.success('Test query updated successfully')
      } else {
        await TestQueryService.createTestQuery(input)
        toast.success('Test query created successfully')
      }

      // Reset form and close modal
      setFormData({
        description: '',
        conversationSnippet: '',
        userIntent: '',
        tags: [],
      })
      setEditingQuery(null)
      onCreateModalClose()
      await loadTestQueries()
    } catch (error) {
      toast.error(
        `Failed to save test query: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this test query?')) {
      return
    }

    try {
      await TestQueryService.deleteTestQuery(id)
      toast.success('Test query deleted successfully')
      await loadTestQueries()
    } catch (error) {
      toast.error(
        `Failed to delete test query: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // Handle edit
  const handleEdit = (query: TestQuery) => {
    setEditingQuery(query)
    setFormData({
      description: query.description,
      conversationSnippet: query.conversationSnippet || '',
      userIntent: query.userIntent || '',
      tags: query.tags,
    })
    onCreateModalOpen()
  }

  // Handle tag input
  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const tag = tagInput.trim()
      if (tag && !formData.tags.includes(tag)) {
        setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }))
        setTagInput('')
      }
    }
  }

  // Remove tag
  const removeTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }))
  }

  // Handle test run creation
  const handleCreateTestRun = async () => {
    if (!testRunName.trim()) {
      toast.error('Test run name is required')
      return
    }

    if (selectedQueries.size === 0) {
      toast.error('At least one test query must be selected')
      return
    }

    try {
      await TestQueryService.createTestRun({
        name: testRunName.trim(),
        description: testRunDescription.trim() || undefined,
        testQueryIds: Array.from(selectedQueries),
      })

      toast.success('Test run created and started successfully')
      setTestRunName('')
      setTestRunDescription('')
      setSelectedQueries(new Set())
      onRunModalClose()
      await loadTestRuns()
    } catch (error) {
      toast.error(
        `Failed to create test run: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // Handle viewing test results
  const handleViewResults = async (testRun: TestRun) => {
    setViewingTestRun(testRun)
    setIsLoadingLogs(true)
    try {
      const result = await RecommendationLogsService.getLogs({
        testRunId: testRun.id,
        limit: 100,
      })
      if (result.success && result.data) {
        setTestRunLogs(result.data.logs)
      } else {
        throw new Error(result.error || 'Failed to load test results')
      }
      onResultsModalOpen()
    } catch (error) {
      toast.error(
        `Failed to load test results: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsLoadingLogs(false)
    }
  }

  // Check if any test runs are still running
  const hasRunningTests = testRuns.some(
    (run) => run.status === 'running' || run.status === 'pending'
  )

  // Load data on mount
  useEffect(() => {
    loadTestQueries()
    loadTestRuns()
  }, [loadTestQueries, loadTestRuns])

  // Poll for updates when there are running tests
  useEffect(() => {
    if (!hasRunningTests || activeTab !== 'results') return

    const interval = setInterval(() => {
      loadTestRuns()
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(interval)
  }, [hasRunningTests, activeTab, loadTestRuns])

  // Refresh test runs when switching to results tab
  useEffect(() => {
    if (activeTab === 'results') {
      loadTestRuns()
    }
  }, [activeTab, loadTestRuns])

  const renderTestQueriesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Test Queries</h3>
        <Button
          color="primary"
          onPress={() => {
            setEditingQuery(null)
            setFormData({
              description: '',
              conversationSnippet: '',
              userIntent: '',
              tags: [],
            })
            onCreateModalOpen()
          }}
        >
          Create New Test Query
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <Table aria-label="Test queries table">
          <TableHeader>
            <TableColumn>DESCRIPTION</TableColumn>
            <TableColumn>INPUT</TableColumn>
            <TableColumn>TAGS</TableColumn>
            <TableColumn>ACTIONS</TableColumn>
          </TableHeader>
          <TableBody>
            {testQueries.map((query) => (
              <TableRow key={query.id}>
                <TableCell>
                  <div className="max-w-xs">
                    <p className="font-medium text-sm">{query.description}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="max-w-xs text-sm">
                    {query.conversationSnippet && (
                      <div className="mb-1">
                        <span className="font-medium">Conversation:</span>{' '}
                        {query.conversationSnippet.substring(0, 50)}...
                      </div>
                    )}
                    {query.userIntent && (
                      <div className="mb-1">
                        <span className="font-medium">Intent:</span>{' '}
                        {query.userIntent.substring(0, 50)}...
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {query.tags.map((tag) => (
                      <Chip key={tag} size="sm" variant="flat">
                        {tag}
                      </Chip>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={() => handleEdit(query)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      onPress={() => handleDelete(query.id!)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )

  const renderTestExecutionTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Test Execution</h3>
        <Button
          color="success"
          onPress={onRunModalOpen}
          isDisabled={testQueries.length === 0}
        >
          Create Test Run
        </Button>
      </div>

      <Card>
        <CardHeader>
          <h4 className="text-md font-semibold">Select Test Queries</h4>
        </CardHeader>
        <CardBody>
          <div className="space-y-2">
            {testQueries.map((query) => (
              <div
                key={query.id}
                className="flex items-center gap-3 p-3 border rounded-lg"
              >
                <input
                  type="checkbox"
                  checked={selectedQueries.has(query.id!)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedQueries)
                    if (e.target.checked) {
                      newSelected.add(query.id!)
                    } else {
                      newSelected.delete(query.id!)
                    }
                    setSelectedQueries(newSelected)
                  }}
                  className="w-4 h-4 accent-primary"
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">{query.description}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {query.tags.map((tag) => (
                      <Chip key={tag} size="sm" variant="flat">
                        {tag}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedQueries.size > 0 && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm font-medium">
                {selectedQueries.size} test{' '}
                {selectedQueries.size === 1 ? 'query' : 'queries'} selected
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )

  const renderTestResultsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Test Runs</h3>
        <Button variant="flat" onPress={loadTestRuns}>
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <Table aria-label="Test runs table">
          <TableHeader>
            <TableColumn>NAME</TableColumn>
            <TableColumn>STATUS</TableColumn>
            <TableColumn>PROGRESS</TableColumn>
            <TableColumn>STARTED</TableColumn>
            <TableColumn>ACTIONS</TableColumn>
          </TableHeader>
          <TableBody>
            {testRuns.map((run) => (
              <TableRow key={run.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{run.name}</p>
                    {run.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {run.description}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    color={
                      run.status === 'completed'
                        ? 'success'
                        : run.status === 'running'
                          ? 'warning'
                          : run.status === 'failed'
                            ? 'danger'
                            : 'default'
                    }
                  >
                    {run.status}
                  </Chip>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p>
                      {run.completedQueries} / {run.totalQueries} completed
                    </p>
                    {run.failedQueries > 0 && (
                      <p className="text-red-600 dark:text-red-400">
                        {run.failedQueries} failed
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-sm">
                    {typeof run.startedAt === 'string'
                      ? new Date(run.startedAt).toLocaleString()
                      : new Date().toLocaleString()}
                  </p>
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="flat"
                    onPress={() => handleViewResults(run)}
                    isLoading={isLoadingLogs && viewingTestRun?.id === run.id}
                  >
                    View Results
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Recommendation Testing</h2>
      </div>

      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => setActiveTab(key as string)}
        aria-label="Testing tabs"
      >
        <Tab key="queries" title="Test Queries">
          {renderTestQueriesTab()}
        </Tab>
        <Tab key="execution" title="Test Execution">
          {renderTestExecutionTab()}
        </Tab>
        <Tab key="results" title="Test Results">
          {renderTestResultsTab()}
        </Tab>
      </Tabs>

      {/* Create/Edit Test Query Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingQuery ? 'Edit Test Query' : 'Create New Test Query'}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Description"
                placeholder="What does this test case cover?"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                isRequired
              />

              <Textarea
                label="Conversation Snippet"
                placeholder="Optional conversation context..."
                value={formData.conversationSnippet}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    conversationSnippet: e.target.value,
                  }))
                }
              />

              <Textarea
                label="User Intent"
                placeholder="What the user wants to convey..."
                value={formData.userIntent}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    userIntent: e.target.value,
                  }))
                }
              />

              <div>
                <Input
                  label="Tags"
                  placeholder="Add tags (press Enter or comma to add)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyPress}
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      onClose={() => removeTag(tag)}
                      variant="flat"
                    >
                      {tag}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={onCreateModalClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSubmit}>
              {editingQuery ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Test Run Modal */}
      <Modal isOpen={isRunModalOpen} onClose={onRunModalClose} size="lg">
        <ModalContent>
          <ModalHeader>Create Test Run</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Test Run Name"
                placeholder="e.g., Weekly Quality Check"
                value={testRunName}
                onChange={(e) => setTestRunName(e.target.value)}
                isRequired
              />

              <Textarea
                label="Description"
                placeholder="Optional description of what you're testing..."
                value={testRunDescription}
                onChange={(e) => setTestRunDescription(e.target.value)}
              />

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm font-medium">
                  {selectedQueries.size} test{' '}
                  {selectedQueries.size === 1 ? 'query' : 'queries'} selected
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  The test run will execute all selected queries and log the
                  results for analysis.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={onRunModalClose}>
              Cancel
            </Button>
            <Button color="success" onPress={handleCreateTestRun}>
              Create & Run Tests
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Test Results Modal */}
      <Modal
        isOpen={isResultsModalOpen}
        onClose={onResultsModalClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>Test Results: {viewingTestRun?.name}</ModalHeader>
          <ModalBody>
            {isLoadingLogs ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : (
              <div className="space-y-4">
                {viewingTestRun && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Status:</span>
                        <Chip
                          size="sm"
                          color={
                            viewingTestRun.status === 'completed'
                              ? 'success'
                              : viewingTestRun.status === 'running'
                                ? 'warning'
                                : viewingTestRun.status === 'failed'
                                  ? 'danger'
                                  : 'default'
                          }
                          className="ml-2"
                        >
                          {viewingTestRun.status}
                        </Chip>
                      </div>
                      <div>
                        <span className="font-medium">Progress:</span>{' '}
                        {viewingTestRun.completedQueries}/
                        {viewingTestRun.totalQueries}
                      </div>
                      <div>
                        <span className="font-medium">Failed:</span>{' '}
                        {viewingTestRun.failedQueries}
                      </div>
                      <div>
                        <span className="font-medium">Started:</span>{' '}
                        {typeof viewingTestRun.startedAt === 'string'
                          ? new Date(viewingTestRun.startedAt).toLocaleString()
                          : new Date().toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}

                {testRunLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No results found for this test run
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h4 className="font-semibold">
                      Recommendation Results ({testRunLogs.length})
                    </h4>
                    {testRunLogs.map((log, index) => (
                      <Card key={log.id || index}>
                        <div className="p-4">
                          {/* Header with status and timing */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="text-xl">
                                {log.success ? '✅' : '❌'}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm text-slate-600 dark:text-slate-400">
                                    {log.requestId}
                                  </span>
                                  <Chip
                                    color={log.success ? 'success' : 'danger'}
                                    variant="flat"
                                    size="sm"
                                  >
                                    {log.success ? 'Success' : 'Failed'}
                                  </Chip>
                                </div>
                                <p className="text-sm text-slate-500">
                                  {new Date(log.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {log.performance?.tokenUsage?.processing
                                ?.estimated_cost_usd?.total && (
                                <Chip color="primary" variant="flat" size="sm">
                                  $
                                  {
                                    log.performance.tokenUsage.processing
                                      .estimated_cost_usd.total
                                  }
                                </Chip>
                              )}
                              <Chip variant="flat" size="sm" color="secondary">
                                {RecommendationLogsService.formatProcessingTime(
                                  log.performance?.processingTimeMs ?? 0
                                )}
                              </Chip>
                            </div>
                          </div>

                          {/* Summary row */}
                          <div className="flex items-center gap-4 text-sm mb-4">
                            <div>
                              <span className="text-slate-500">Results:</span>
                              <span className="ml-1 font-medium">
                                {log.results?.recommendationCount ?? 0}{' '}
                                recommendations
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-500">
                                Candidates:
                              </span>
                              <span className="ml-1 font-medium">
                                {log.processing?.candidatesFound ?? 0}
                              </span>
                            </div>
                          </div>

                          {/* Error display */}
                          {!log.success && log.error && (
                            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-950 dark:border-red-800">
                              <div className="text-sm text-red-700 dark:text-red-300">
                                <strong>Error:</strong> {log.error}
                              </div>
                            </div>
                          )}

                          {/* Input details */}
                          <div className="space-y-3 mb-4">
                            {log.input.conversationSnippet && (
                              <div>
                                <span className="font-medium text-slate-600 dark:text-slate-400 text-sm">
                                  Conversation Snippet:
                                </span>
                                <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                                  {log.input.conversationSnippet}
                                </div>
                              </div>
                            )}
                            {log.input.userIntent && (
                              <div>
                                <span className="font-medium text-slate-600 dark:text-slate-400 text-sm">
                                  User Intent:
                                </span>
                                <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                                  {log.input.userIntent}
                                </div>
                              </div>
                            )}
                            {log.input.preferences && (
                              <div>
                                <span className="font-medium text-slate-600 dark:text-slate-400 text-sm">
                                  Preferences:
                                </span>
                                <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                                  {JSON.stringify(
                                    log.input.preferences,
                                    null,
                                    2
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Search candidates preview */}
                          {log.processing?.searchCandidateIds &&
                            log.processing.searchCandidateIds.length > 0 && (
                              <div className="mb-4">
                                <SearchCandidatesPreview
                                  candidateIds={
                                    log.processing.searchCandidateIds
                                  }
                                />
                              </div>
                            )}

                          {/* Recommendations */}
                          {log.results?.recommendations &&
                            log.results.recommendations.length > 0 && (
                              <div className="space-y-3">
                                <div className="text-sm font-medium">
                                  Recommendations (
                                  {log.results.recommendations.length}):
                                </div>
                                <div className="space-y-3">
                                  {log.results.recommendations.map(
                                    (
                                      rec: RecommendationLogEntry['results']['recommendations'][0],
                                      recIndex: number
                                    ) => {
                                      const gif = gifs.find(
                                        (g) => g.id === rec.gifId
                                      )
                                      return (
                                        <div
                                          key={recIndex}
                                          className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                                        >
                                          <div className="flex gap-3">
                                            {/* GIF Thumbnail */}
                                            <div className="flex-shrink-0">
                                              {gif ? (
                                                <GifThumbnail
                                                  gif={gif}
                                                  className="w-20 h-20 rounded-lg"
                                                />
                                              ) : (
                                                <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                                                  <span className="text-xs text-gray-500">
                                                    No preview
                                                  </span>
                                                </div>
                                              )}
                                            </div>

                                            {/* Recommendation Details */}
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-start justify-between mb-2">
                                                <div className="font-medium text-sm">
                                                  #{rec.rank} - {rec.gifId}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <Button
                                                    size="sm"
                                                    variant="flat"
                                                    color="primary"
                                                    onPress={async () => {
                                                      const ok =
                                                        await copyToClipboard(
                                                          rec.gifId
                                                        )
                                                      if (ok) {
                                                        toast.success(
                                                          `Copied ID: ${rec.gifId}`
                                                        )
                                                      } else {
                                                        toast.error(
                                                          'Failed to copy ID'
                                                        )
                                                      }
                                                    }}
                                                  >
                                                    Copy ID
                                                  </Button>
                                                  <Chip
                                                    size="sm"
                                                    variant="flat"
                                                    color="primary"
                                                  >
                                                    Rank {rec.rank}
                                                  </Chip>
                                                </div>
                                              </div>

                                              {gif && (
                                                <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                                                  <strong>Description:</strong>{' '}
                                                  {gifDisplayTitle(gif)}
                                                </div>
                                              )}

                                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                                <strong>Reasoning:</strong>{' '}
                                                {rec.reasoning}
                                              </div>

                                              {rec.strengths &&
                                                rec.strengths.length > 0 && (
                                                  <div className="text-xs text-green-600 dark:text-green-400 mb-1">
                                                    <strong>Strengths:</strong>{' '}
                                                    {rec.strengths.join(', ')}
                                                  </div>
                                                )}

                                              {rec.limitations &&
                                                rec.limitations.length > 0 && (
                                                  <div className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                                                    <strong>
                                                      Limitations:
                                                    </strong>{' '}
                                                    {rec.limitations.join(', ')}
                                                  </div>
                                                )}

                                              {gif &&
                                                gif.useCases &&
                                                gif.useCases.length > 0 && (
                                                  <div>
                                                    <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                                                      <strong>
                                                        Use Cases:
                                                      </strong>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                      {gif.useCases
                                                        .slice(0, 3)
                                                        .map(
                                                          (
                                                            useCase,
                                                            useCaseIndex
                                                          ) => (
                                                            <Chip
                                                              key={useCaseIndex}
                                                              size="sm"
                                                              variant="flat"
                                                              className="text-xs"
                                                            >
                                                              {useCase}
                                                            </Chip>
                                                          )
                                                        )}
                                                      {gif.useCases.length >
                                                        3 && (
                                                        <Chip
                                                          size="sm"
                                                          variant="flat"
                                                          className="text-xs"
                                                        >
                                                          +
                                                          {gif.useCases.length -
                                                            3}{' '}
                                                          more
                                                        </Chip>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    }
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Performance metrics */}
                          {(log.performance || log.processing) && (
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                              <PerformanceMetrics
                                processingTimeMs={
                                  log.performance?.processingTimeMs
                                }
                                timingBreakdown={
                                  log.performance?.timingBreakdown
                                }
                                tokenUsage={log.performance?.tokenUsage}
                                documentsEvaluated={
                                  log.processing?.unifiedAnalysis
                                    ?.totalDocumentsEvaluated ??
                                  log.processing?.candidatesFound
                                }
                                finalRecommendationCount={
                                  log.processing?.unifiedAnalysis
                                    ?.finalRecommendationCount ??
                                  log.results?.recommendationCount
                                }
                                candidatesFound={
                                  log.processing?.candidatesFound
                                }
                              />
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={onResultsModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
